import {
  createInvitedHouseholdSchema,
  householdMemberLimit,
  MAX_HOUSEHOLD_NAME_LENGTH,
  parseEmailList,
  type AdminHouseholdRow,
  type CreateInvitedHouseholdRequest,
} from '@platewell/schema';
import { Link, useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { DataTable, type DataTableColumn } from '../components/data-table.js';
import { Dialog } from '../components/dialog.js';
import { RelativeTime } from '../components/relative-time.js';
import { ApiError, createInvitedHousehold, listAdminHouseholds } from '../lib/api.js';
import { useAdminList } from '../lib/admin-list.js';
import { adminHouseholdsRoute, type AdminListSearch } from '../router.js';

function columnsFor(search: AdminListSearch): DataTableColumn<AdminHouseholdRow>[] {
  return [
    {
      key: 'name',
      label: 'Household name',
      // The link is the real affordance — the row's own click handler is a convenience on
      // top of it, and a `<tr>` cannot be tabbed to or announced as something that opens.
      // It carries the list's search params so the detail page can send the agent back to
      // the page and query they came from.
      render: (row) => (
        <>
          <Link
            className="data-table-strong data-table-link"
            to="/admin/households/$householdId"
            params={{ householdId: row.id }}
            search={search}
          >
            {row.name}
          </Link>
          {row.deleted && <span className="data-table-flag">deleted</span>}
        </>
      ),
    },
    {
      key: 'members',
      label: 'Members',
      align: 'right',
      render: (row) => <span className="data-table-num">{row.memberCount}</span>,
    },
    {
      key: 'recipes',
      label: 'Recipes',
      align: 'right',
      // Read from the projected read model, so a household whose projection is behind can
      // report fewer recipes than its CRDT actually holds.
      render: (row) => <span className="data-table-num">{row.recipeCount}</span>,
    },
    {
      key: 'createdAt',
      label: 'Since',
      render: (row) => <RelativeTime value={row.createdAt} />,
    },
  ];
}

export function AdminHouseholds() {
  const search = adminHouseholdsRoute.useSearch();
  const routeNavigate = useNavigate({ from: '/admin/households' });

  const navigate = useCallback(
    (update: (previous: AdminListSearch) => AdminListSearch, replace: boolean) => {
      void routeNavigate({ search: update, replace });
    },
    [routeNavigate],
  );

  const list = useAdminList<AdminHouseholdRow>({
    search,
    defaultSort: 'name',
    fetchPage: listAdminHouseholds,
    navigate,
    errorLabel: 'Could not load households',
  });

  // Rebuilt only when the search params move, because every name cell links back through
  // them — a column array rebuilt on each render would re-render every row for nothing.
  const columns = useMemo(() => columnsFor(search), [search]);

  const openHousehold = useCallback(
    (row: AdminHouseholdRow) => {
      void routeNavigate({
        to: '/admin/households/$householdId',
        params: { householdId: row.id },
        search,
      });
    },
    [routeNavigate, search],
  );

  const [creating, setCreating] = useState(false);

  // Straight to the new household: its page shows the plan and the pending invites as the
  // server wrote them, which is the confirmation the agent actually needs.
  const create = useCallback(
    async (request: CreateInvitedHouseholdRequest) => {
      const { id } = await createInvitedHousehold(request);
      setCreating(false);
      void routeNavigate({
        to: '/admin/households/$householdId',
        params: { householdId: id },
        search,
      });
    },
    [routeNavigate, search],
  );

  return (
    <>
      <div className="admin-page-head">
        <h1>Households</h1>
        <span className="admin-range">{list.rangeLabel}</span>
      </div>

      <div className="admin-toolbar">
        <input
          className="search-input"
          type="search"
          aria-label="Search households"
          placeholder="Search household name, member email…"
          value={list.term}
          onChange={(event) => list.setTerm(event.target.value)}
        />
        <span className="spacer" />
        <button type="button" className="btn" onClick={() => setCreating(true)}>
          Invite a household
        </button>
      </div>

      {list.error != null && <p className="error-text">{list.error}</p>}

      <DataTable
        caption="Households"
        columns={columns}
        rows={list.rows}
        rowKey={(row) => row.id}
        total={list.total}
        page={list.page}
        pageSize={list.pageSize}
        sort={list.sort}
        dir={list.dir}
        loading={list.loading}
        onSortChange={list.onSortChange}
        onPageChange={list.onPageChange}
        density="compact"
        onRowClick={openHousehold}
        rowClassName={(row) => (row.deleted ? 'data-table-row--deleted' : undefined)}
        emptyLabel={list.q === '' ? 'No households yet.' : `Nothing here for “${list.q}”.`}
      />

      {creating && (
        <CreateInvitedHouseholdDialog onClose={() => setCreating(false)} onCreate={create} />
      )}
    </>
  );
}

const CREATE_REFUSALS: Record<string, string> = {
  invalid_request: 'Check the household name and the addresses, then try again.',
  forbidden: 'Your session cannot do this. Sign in again as yourself.',
};

const emailSchema = createInvitedHouseholdSchema.shape.emails.element;

/**
 * Asks for a household name, the addresses to invite and whether to comp the household.
 *
 * The addresses are parsed as they are typed, so a typo is pointed out before submitting
 * rather than coming back as a bare refusal for the whole list.
 */
function CreateInvitedHouseholdDialog({
  onClose,
  onCreate,
}: {
  onClose: () => void;
  onCreate: (request: CreateInvitedHouseholdRequest) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [emailText, setEmailText] = useState('');
  const [complimentary, setComplimentary] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const emails = useMemo(() => parseEmailList(emailText), [emailText]);
  const invalid = emails.filter((email) => !emailSchema.safeParse(email).success);
  const memberLimit = householdMemberLimit(complimentary ? 'pro' : 'free');
  const tooMany = emails.length > memberLimit;

  const trimmedName = name.trim();
  const canSubmit =
    trimmedName !== '' && emails.length > 0 && invalid.length === 0 && !tooMany && !busy;

  function submit() {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    onCreate({ name: trimmedName, emails, complimentary }).catch((cause: unknown) => {
      const code = cause instanceof ApiError ? cause.code : '';
      setError(
        CREATE_REFUSALS[code] ??
          (cause instanceof Error ? cause.message : 'Could not create the household.'),
      );
      setBusy(false);
    });
  }

  return (
    <Dialog onClose={busy ? () => undefined : onClose} aria-label="Invite a household">
      <h2>Invite a household</h2>
      <p className="hint">
        Creates a new household. Everyone below is emailed an invite and joins as an owner.
      </p>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <label className="field">
          <span className="label">Household name</span>
          <input
            className="input"
            value={name}
            autoFocus
            maxLength={MAX_HOUSEHOLD_NAME_LENGTH}
            placeholder="The Smiths"
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <label className="field" style={{ marginTop: 14 }}>
          <span className="label">Email addresses</span>
          <textarea
            className="textarea"
            value={emailText}
            rows={4}
            placeholder={'sam@example.com\nalex@example.com'}
            onChange={(event) => setEmailText(event.target.value)}
          />
        </label>
        {invalid.length > 0 ? (
          <p className="error-text">Not an email address: {invalid.join(', ')}</p>
        ) : tooMany ? (
          <p className="error-text">
            {complimentary
              ? `A household holds at most ${memberLimit} people; ${emails.length} listed.`
              : `A free household holds ${memberLimit} person; ${emails.length} listed.`}
          </p>
        ) : (
          <p className="hint">
            {emails.length === 0
              ? 'One per line, or separated by commas.'
              : `${emails.length} ${emails.length === 1 ? 'person' : 'people'} will be invited.`}
          </p>
        )}
        <label className="form-checkbox">
          <input
            type="checkbox"
            checked={complimentary}
            onChange={(event) => setComplimentary(event.target.checked)}
          />
          <span>Give a complimentary subscription.</span>
        </label>
        {error != null && <p className="error-text">{error}</p>}
        <div className="row" style={{ marginTop: 18 }}>
          <span className="spacer" />
          <button type="button" className="btn ghost" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn primary" disabled={!canSubmit}>
            {busy ? 'Inviting…' : 'Create and invite'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
